import React from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  autocompleteClasses,
  Box,
  useMediaQuery,
  CircularProgress,
  Grid,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ArrowDropDown,
  ChevronLeft,
  ChevronRight,
  ExpandLess,
} from '@mui/icons-material';
import { TableFilter } from '../table/TabIndexTableToolbar';

interface CalendarHeaderProps {
  expanded?: boolean;
  setExpanded?: Function;
  title?: string;
  children?: React.ReactNode;
  onPrev?: () => void;
  onNext?: () => void;
  onThisWeek?: () => void;
  month?: boolean;
  filters?: TableFilter[][];
  accordion?: boolean;
}

export const CalendarHeader = ({
  children,
  onPrev = () => {},
  onNext = () => {},
  onThisWeek = () => {},
  title = '',
  month = false,
  filters,
  accordion = false,
  expanded = false,
  setExpanded = () => {},
}: CalendarHeaderProps) => {
  const theme = useTheme();
  const downMd = useMediaQuery(theme.breakpoints.down('md'));
  // const [expanded, setExpanded] = React.useState<boolean>(false);
  if (accordion) {
    return (
      <Box
        sx={{
          width: '100%',
          zIndex: 9999,
          backgroundColor: 'white',
          pt: expanded ? 1 : undefined,
          borderTop: theme => `1px solid ${theme.palette.divider}`,
          boxShadow: expanded ? '0 0 0 99999px rgba(0, 0, 0, .7)' : undefined,
        }}
      >
        <Accordion
          disableGutters
          square
          sx={{
            filter: `drop-shadow(0px -4px 19px rgba(0, 0, 0, 0.08))`,
            borderRadius: 0,
            boxShadow: 'unset',
          }}
          expanded={expanded}
          onClick={() => setExpanded((pv: any) => !pv)}
        >
          <AccordionSummary id={'calendarExpand'} expandIcon={<ExpandLess />}>
            <Grid container alignItems="center" spacing={0} sx={{ mx: 1 }}>
              <IconButton
                size="small"
                sx={{
                  marginX: 0.5,
                  color: `${theme.palette.text.primary}66`,
                  '&:hover': {
                    color: theme.palette.secondary.main,
                  },
                }}
                onClick={e => {
                  e.stopPropagation();
                  onPrev();
                }}
              >
                <ChevronLeft
                  sx={{
                    color: 'inherit',
                  }}
                />
              </IconButton>

              <Typography
                sx={{
                  lineHeight: 1,
                  '&:hover': {
                    color: theme.palette.secondary.main,
                    textDecoration: 'underline',
                  },
                }}
                onClick={e => {
                  e.stopPropagation();
                  onThisWeek();
                }}
              >
                {month ? 'This Month' : 'This Week'}
              </Typography>

              <IconButton
                size="small"
                sx={{
                  marginX: 0.5,
                  color: `${theme.palette.text.primary}66`,
                  '&:hover': {
                    color: theme.palette.secondary.main,
                  },
                }}
                onClick={e => {
                  e.stopPropagation();
                  onNext();
                }}
              >
                <ChevronRight
                  sx={{
                    color: 'inherit',
                  }}
                />
              </IconButton>

              <Typography
                variant="overline"
                color="textSecondary"
                sx={{
                  textTransform: 'uppercase',
                  lineHeight: 1,
                }}
              >
                {title}
              </Typography>
              {expanded && !downMd && (
                <Grid item sx={{ marginLeft: 'auto', flex: 1, mr: 2 }}>
                  <Grid
                    container
                    direction="row-reverse"
                    alignItems="center"
                    spacing={2}
                  >
                    {filters?.map((filter, index) =>
                      filter.map((f, i) => (
                        <Grid
                          item
                          sx={{
                            // display: index === value ? 'block' : 'none',
                            flex: downMd ? 1 : 0,
                          }}
                          key={`${i}-${index}`}
                          onClick={event => {
                            event.preventDefault();
                            event.stopPropagation();
                          }}
                        >
                          <Autocomplete
                            id={`filter-${index}-${i}`}
                            disablePortal
                            size="small"
                            disableClearable
                            options={f.options}
                            onChange={(event, value) =>
                              f.onChange(event, value)
                            }
                            value={f.defaultOption}
                            getOptionLabel={(option: any) => {
                              if (typeof option === 'string') {
                                return option;
                              } else {
                                return (
                                  option?.label ||
                                  option?.title ||
                                  option?.name ||
                                  ''
                                );
                              }
                            }}
                            renderInput={params => (
                              <TextField
                                {...params}
                                size="small"
                                label={f.label || ''}
                                InputProps={{
                                  ...params.InputProps,
                                }}
                              />
                            )}
                            popupIcon={
                              f.loading ? (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 24,
                                    height: 24,
                                  }}
                                >
                                  <CircularProgress color="inherit" size={18} />
                                </Box>
                              ) : (
                                <ArrowDropDown />
                              )
                            }
                            fullWidth
                            sx={{
                              [`.${autocompleteClasses.inputRoot}`]: {
                                [`.${autocompleteClasses.input}`]: {
                                  width: 'fit-content',
                                },
                              },
                            }}
                          />
                          {/* <ControlledAutoComplete {...f} /> */}
                        </Grid>
                      ))
                    )}
                  </Grid>
                </Grid>
              )}
            </Grid>
          </AccordionSummary>

          <AccordionDetails
            sx={{
              padding: 0,
              backgroundColor: 'white',
            }}
          >
            {children}
          </AccordionDetails>
        </Accordion>
      </Box>
    );
  } else {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          overflowY: 'scroll',
          backgroundColor: 'white',
        }}
      >
        <Grid container sx={{ py: 1 }}>
          <Grid item md={4}>
            <Grid container alignItems="center" spacing={0} sx={{ mx: 1 }}>
              <IconButton
                size="small"
                sx={{
                  marginX: 0.5,
                  color: `${theme.palette.text.primary}66`,
                  '&:hover': {
                    color: theme.palette.secondary.main,
                  },
                }}
                onClick={e => {
                  e.stopPropagation();
                  onPrev();
                }}
              >
                <ChevronLeft
                  sx={{
                    color: 'inherit',
                  }}
                />
              </IconButton>

              <Typography
                sx={{
                  lineHeight: 1,
                  '&:hover': {
                    color: theme.palette.secondary.main,
                    textDecoration: 'underline',
                  },
                }}
                onClick={e => {
                  e.stopPropagation();
                  onThisWeek();
                }}
              >
                {month ? 'This Month' : 'This Week'}
              </Typography>

              <IconButton
                size="small"
                sx={{
                  marginX: 0.5,
                  color: `${theme.palette.text.primary}66`,
                  '&:hover': {
                    color: theme.palette.secondary.main,
                  },
                }}
                onClick={e => {
                  e.stopPropagation();
                  onNext();
                }}
              >
                <ChevronRight
                  sx={{
                    color: 'inherit',
                  }}
                />
              </IconButton>

              <Typography
                variant="overline"
                color="textSecondary"
                sx={{
                  textTransform: 'uppercase',
                  lineHeight: 1,
                }}
              >
                {title}
              </Typography>
            </Grid>
          </Grid>
          {!downMd && (
            <Grid item sx={{ marginLeft: 'auto', flex: 1, mr: 2 }}>
              <Grid
                container
                direction="row-reverse"
                alignItems="center"
                spacing={2}
              >
                {filters?.map((filter, index) =>
                  filter.map((f, i) => (
                    <Grid
                      item
                      sx={{
                        // display: index === value ? 'block' : 'none',
                        flex: downMd ? 1 : 0,
                      }}
                      key={`${i}-${index}`}
                      onClick={event => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                    >
                      <Autocomplete
                        id={`filter-${index}-${i}`}
                        disablePortal
                        size="small"
                        disableClearable
                        options={f.options}
                        onChange={(event, value) => f.onChange(event, value)}
                        value={f.defaultOption}
                        getOptionLabel={(option: any) => {
                          if (typeof option === 'string') {
                            return option;
                          } else {
                            return (
                              option?.label ||
                              option?.title ||
                              option?.name ||
                              ''
                            );
                          }
                        }}
                        renderInput={params => (
                          <TextField
                            {...params}
                            size="small"
                            label={f.label || ''}
                            InputProps={{
                              ...params.InputProps,
                            }}
                          />
                        )}
                        popupIcon={
                          f.loading ? (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 24,
                                height: 24,
                              }}
                            >
                              <CircularProgress color="inherit" size={18} />
                            </Box>
                          ) : (
                            <ArrowDropDown />
                          )
                        }
                        fullWidth
                        sx={{
                          [`.${autocompleteClasses.inputRoot}`]: {
                            [`.${autocompleteClasses.input}`]: {
                              width: 'fit-content',
                            },
                          },
                        }}
                      />
                      {/* <ControlledAutoComplete {...f} /> */}
                    </Grid>
                  ))
                )}
              </Grid>
            </Grid>
          )}
        </Grid>
        {children}
      </Box>
    );
  }
};
